import React, { useState } from 'react';
import { MapPin, Mail, Phone, Send } from 'lucide-react';
import { contactInfo } from '../data/mockData';
import { toast } from 'sonner';
import LightEffects from './LightEffects';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Mock submission - will be replaced with actual backend API
    setTimeout(() => {
      console.log('Form submitted:', formData);
      toast.success('Thank you! We will get back to you soon.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        message: ''
      });
      setIsSubmitting(false);
    }, 1500);
  };

  return (
    <section id="contact" className="relative bg-black py-24 md:py-32">
      {/* Light Effects */}
      <LightEffects variant="beams" />
      <LightEffects variant="radial" />
      
      <div className="container mx-auto px-[7.6923%]">
        {/* Section Header */}
        <div className="text-center mb-20">
          <h2 className="text-5xl md:text-6xl font-semibold text-white mb-6 tracking-tight">
            Get in Touch
          </h2>
          <div className="h-1 w-24 bg-blue-500 mx-auto mb-6"></div>
          <p className="text-xl text-white/70 max-w-3xl mx-auto leading-relaxed">
            Ready to illuminate your project? Contact us to discuss your lighting needs
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 max-w-7xl mx-auto">
          {/* Contact Information */}
          <div className="space-y-10">
            {/* India Office */}
            <div className="bg-gradient-to-br from-white/5 to-transparent border border-white/10 p-8">
              <div className="flex items-start gap-4 mb-4">
                <MapPin size={24} className="text-blue-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-semibold text-white mb-3">
                    {contactInfo.india.title}
                  </h3>
                  <p className="text-base text-white/70 leading-relaxed">
                    {contactInfo.india.address}<br />
                    {contactInfo.india.city}<br />
                    {contactInfo.india.country}
                  </p>
                </div>
              </div>
            </div>

            {/* Germany R&D Centre */}
            <div className="bg-gradient-to-br from-white/5 to-transparent border border-white/10 p-8">
              <div className="flex items-start gap-4 mb-4">
                <MapPin size={24} className="text-blue-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-semibold text-white mb-3">
                    {contactInfo.germany.title}
                  </h3>
                  <p className="text-base text-white/70 leading-relaxed">
                    {contactInfo.germany.address}<br />
                    {contactInfo.germany.city}<br />
                    {contactInfo.germany.country}
                  </p>
                </div>
              </div>
            </div>

            {/* Email & Phone */}
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <Mail size={24} className="text-blue-500" />
                <a
                  href={`mailto:${contactInfo.email}`}
                  className="text-lg text-white/85 hover:text-blue-500 transition-colors duration-300"
                >
                  {contactInfo.email}
                </a>
              </div>
              <div className="flex items-center gap-4">
                <Phone size={24} className="text-blue-500" />
                <a
                  href={`tel:${contactInfo.phone}`}
                  className="text-lg text-white/85 hover:text-blue-500 transition-colors duration-300"
                >
                  {contactInfo.phone}
                </a>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-gradient-to-br from-white/5 to-transparent border border-white/10 p-10">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-white/85 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-black border border-white/20 text-white placeholder-white/40 focus:border-blue-500 focus:outline-none transition-colors duration-300"
                  style={{ borderRadius: '0px' }}
                  placeholder="Your name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-white/85 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-black border border-white/20 text-white placeholder-white/40 focus:border-blue-500 focus:outline-none transition-colors duration-300"
                  style={{ borderRadius: '0px' }}
                  placeholder="your.email@example.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-white/85 mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-black border border-white/20 text-white placeholder-white/40 focus:border-blue-500 focus:outline-none transition-colors duration-300"
                  style={{ borderRadius: '0px' }}
                  placeholder="+91 1234567890"
                />
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-medium text-white/85 mb-2">
                  Company
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-black border border-white/20 text-white placeholder-white/40 focus:border-blue-500 focus:outline-none transition-colors duration-300"
                  style={{ borderRadius: '0px' }}
                  placeholder="Your company name"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-white/85 mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="w-full px-4 py-3 bg-black border border-white/20 text-white placeholder-white/40 focus:border-blue-500 focus:outline-none transition-colors duration-300 resize-none"
                  style={{ borderRadius: '0px' }}
                  placeholder="Tell us about your project..."
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-blue-600 text-white font-medium hover:bg-blue-700 transition-all duration-400 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ borderRadius: '0px' }}
              >
                {isSubmitting ? (
                  'Sending...'
                ) : (
                  <>
                    Send Message
                    <Send size={20} />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
